import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';
import { toast } from 'sonner';
import { 
  ShoppingCart, Truck, Package, TrendingUp, 
  DollarSign, Calendar, Loader2, Download
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';

export default function ReorderSuggestions() {
  const { user } = useAuth();
  const [suggestions, setSuggestions] = useState([]);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      
      const [suggestionsRes, reportRes] = await Promise.all([
        api.get('/inventory-intel/reorder-suggestions'),
        api.get('/inventory-intel/reorder-report')
      ]);
      
      setSuggestions(suggestionsRes.data || []);
      setReport(reportRes.data);
    } catch (error) {
      toast.error('Failed to load reorder data');
    } finally {
      setLoading(false);
    }
  };

  const getConfidenceColor = (score) => {
    if (score >= 0.8) return 'bg-green-100 text-green-800';
    if (score >= 0.6) return 'bg-yellow-100 text-yellow-800';
    return 'bg-gray-100 text-gray-800';
  };

  const getUrgencyBadge = (days) => {
    if (days <= 7) return <Badge className="bg-red-100 text-red-800">Urgent</Badge>;
    if (days <= 14) return <Badge className="bg-yellow-100 text-yellow-800">Soon</Badge>;
    return <Badge className="bg-blue-100 text-blue-800">Planned</Badge>;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin" />
      </div>
    );
  }

  // Categorize suggestions
  const urgent = suggestions.filter(s => s.days_of_stock_remaining <= 7);
  const planned = suggestions.filter(s => s.days_of_stock_remaining > 7 && s.days_of_stock_remaining <= 14);
  const future = suggestions.filter(s => s.days_of_stock_remaining > 14);

  return (
    <div className="max-w-6xl mx-auto p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold">Reorder Suggestions</h1>
          <p className="text-gray-600">AI-powered reorder recommendations</p>
        </div>
        <Button onClick={loadData} variant="outline">
          <Download className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Summary Cards */}
      {report && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Urgent Orders</p>
                  <p className="text-2xl font-bold text-red-600">{urgent.length}</p>
                </div>
                <ShoppingCart className="w-8 h-8 text-red-500" />
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Planned Orders</p>
                  <p className="text-2xl font-bold text-yellow-600">{planned.length}</p>
                </div>
                <Calendar className="w-8 h-8 text-yellow-500" />
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Future Orders</p>
                  <p className="text-2xl font-bold text-blue-600">{future.length}</p>
                </div>
                <Package className="w-8 h-8 text-blue-500" />
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Est. Total Cost</p>
                  <p className="text-2xl font-bold text-green-600">
                    ${report.total_estimated_cost?.toLocaleString()}
                  </p>
                </div>
                <DollarSign className="w-8 h-8 text-green-500" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Suggestions Tabs */}
      <Tabs defaultValue="urgent">
        <TabsList className="mb-4">
          <TabsTrigger value="urgent">
            <ShoppingCart className="w-4 h-4 mr-2" />
            Urgent ({urgent.length})
          </TabsTrigger>
          <TabsTrigger value="planned">
            <Calendar className="w-4 h-4 mr-2" />
            Planned ({planned.length})
          </TabsTrigger>
          <TabsTrigger value="future">
            <Package className="w-4 h-4 mr-2" />
            Future ({future.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="urgent">
          <SuggestionsTable suggestions={urgent} getUrgencyBadge={getUrgencyBadge} getConfidenceColor={getConfidenceColor} />
        </TabsContent>

        <TabsContent value="planned">
          <SuggestionsTable suggestions={planned} getUrgencyBadge={getUrgencyBadge} getConfidenceColor={getConfidenceColor} />
        </TabsContent>

        <TabsContent value="future">
          <SuggestionsTable suggestions={future} getUrgencyBadge={getUrgencyBadge} getConfidenceColor={getConfidenceColor} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function SuggestionsTable({ suggestions, getUrgencyBadge, getConfidenceColor }) {
  if (suggestions.length === 0) {
    return (
      <div className="text-center py-12 bg-gray-50 rounded-lg">
        <Package className="w-12 h-12 text-gray-400 mx-auto mb-4" />
        <p className="text-gray-600">No reorder suggestions in this category</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg border overflow-hidden">
      <table className="w-full">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Product</th>
            <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Current Stock</th>
            <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Daily Sales</th>
            <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Days Left</th>
            <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Suggested Qty</th>
            <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Supplier</th>
            <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Confidence</th>
          </tr>
        </thead>
        <tbody className="divide-y">
          {suggestions.map((suggestion) => (
            <tr key={suggestion.id} className="hover:bg-gray-50">
              <td className="px-4 py-3">
                <div>
                  <p className="font-medium">{suggestion.product_name}</p>
                  <p className="text-sm text-gray-500">{suggestion.sku}</p>
                </div>
              </td>
              <td className="px-4 py-3">
                <span className={suggestion.current_stock <= 10 ? 'text-red-600 font-bold' : ''}>
                  {suggestion.current_stock}
                </span>
              </td>
              <td className="px-4 py-3">
                <div className="flex items-center gap-1">
                  <TrendingUp className="w-4 h-4 text-green-500" />
                  {suggestion.avg_daily_sales.toFixed(1)}
                </div>
              </td>
              <td className="px-4 py-3">
                {getUrgencyBadge(suggestion.days_of_stock_remaining)}
                <span className="ml-2 text-sm text-gray-600">
                  {suggestion.days_of_stock_remaining.toFixed(1)} days
                </span>
              </td>
              <td className="px-4 py-3">
                <span className="font-bold text-blue-600">
                  {suggestion.suggested_quantity}
                </span>
              </td>
              <td className="px-4 py-3">
                {suggestion.supplier_name ? (
                  <div className="flex items-center gap-1">
                    <Truck className="w-4 h-4 text-gray-400" />
                    {suggestion.supplier_name}
                  </div>
                ) : (
                  <span className="text-gray-400">No supplier</span>
                )}
              </td>
              <td className="px-4 py-3">
                <Badge className={getConfidenceColor(suggestion.confidence_score)}>
                  {(suggestion.confidence_score * 100).toFixed(0)}%
                </Badge>
                {suggestion.factors?.length > 0 && (
                  <p className="text-xs text-gray-500 mt-1">
                    {suggestion.factors.join(', ')}
                  </p>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
